import axios from "axios";
import { API, PART_FLAGS } from "../shared/constants";

// Shared "one inspection per Asset Register tab" logic.
//
// A tab is a SECTION of the site — a SiteArea — which is exactly what
// Inspection.site_area models on the backend. Category (Fire Doors,
// Extinguishers, ...) is a COLUMN within a tab, not a tab of its own — a single
// tab legitimately spans several EFSM categories.
//
// Inspection.is_unsectioned_units is always false now; it is kept on the model
// because inspections raised against the old pinned tabs still carry it.
//
// This lives here rather than in each caller because the scoping rules are
// subtle and were previously duplicated (and drifted) between the per-tab
// modal, the batch modal and the Inspections wizard.

// Mirrors the tab list built by AssetRegisterTabs, so an inspection can be
// scoped to exactly the same set of assets the user sees under that tab.
// Every register tab is a real SiteArea section. The old pinned "Unsectioned"
// tabs — one for loose whole-building assets, one for tenants with no section —
// have been retired now that everything lives in a named section.
export function buildRegisterTabs(site, areas) {
  if (!site) return [];
  const tabs = [];
  areas.forEach((a) => {
    if (site[PART_FLAGS[a.area_type]]) {
      tabs.push({
        key: String(a.id),
        label: a.name,
        kind: "area",
        areaType: a.area_type,
        area: a,
      });
    }
  });
  return tabs;
}

// One round-trip for the whole site, then every tab is scoped client-side.
// Creating N inspections must not mean 3N HTTP calls.
export async function loadTabScopeContext(siteId) {
  const [efsmsRes, unitsRes] = await Promise.all([
    axios.get(`${API}/site-efsms/?site=${siteId}`),
    axios.get(`${API}/site-units/?site=${siteId}`),
  ]);
  const allEfsms = (efsmsRes.data || []).filter((e) => e.site === siteId);
  const units = unitsRes.data || [];
  return {
    allEfsms,
    unitAreaById: new Map(units.map((u) => [u.id, u.area])),
  };
}

// A category's testing frequency lives on the SITE-LEVEL EFSM row (no unit, no
// area) — the single source of truth, matching SiteEFSMViewSet.SCHEDULE_FIELDS.
// It applies to that category's assets in every register.
//
// A category with no site-level row, or a site-level row with nothing ticked at
// all, counts as unconfigured and is included: better to over-include than to
// silently drop those assets from every inspection type forever.
export function isCategoryScheduled(ctx, categoryId, inspectionType) {
  const flagField = {
    monthly: "is_monthly",
    bi_annual: "is_bi_annual",
    annual: "is_annual",
  }[inspectionType];
  if (!flagField) return false;
  const siteLevel = ctx.allEfsms.filter(
    (e) => !e.unit && !e.area && e.category === categoryId,
  );
  if (siteLevel.length === 0) return true;
  if (!siteLevel.some((e) => e.is_monthly || e.is_bi_annual || e.is_annual)) return true;
  return siteLevel.some((e) => e[flagField]);
}

// The EFSMs whose assets appear under one tab — same scoping the register uses.
export function efsmsForTab(ctx, tab) {
  const areaId = tab.area.id;
  // EFSMs attached directly to the area, plus those on any unit belonging to
  // it, so a tenant-category tab returns every unit's assets combined.
  return ctx.allEfsms.filter(
    (e) => e.area === areaId || (e.unit && ctx.unitAreaById.get(e.unit) === areaId),
  );
}

export function tabAssetIds(ctx, tab, inspectionType) {
  return efsmsForTab(ctx, tab)
    .filter((e) => isCategoryScheduled(ctx, e.category, inspectionType))
    .flatMap((e) => (e.assets || []).map((a) => a.id));
}

// Creates one inspection scoped to one tab and attaches its assets.
// Returns null when the tab has no assets due for this type and skipIfEmpty is
// set — an empty inspection is noise in every downstream list.
export async function createTabInspection({
  siteId,
  tab,
  ctx,
  inspectionType,
  date,
  inspectorName = "",
  assignedTo = null,
  additionalTechs = [],
  notes = "",
  flexible = false,
  skipIfEmpty = false,
}) {
  const assetIds = tabAssetIds(ctx, tab, inspectionType);
  if (skipIfEmpty && assetIds.length === 0) return null;

  const res = await axios.post(`${API}/inspections/`, {
    site: siteId,
    site_area: tab.area.id,
    is_unsectioned_units: false,
    inspection_type: inspectionType,
    date,
    inspector_name: inspectorName,
    assigned_to: assignedTo || null,
    additional_techs: additionalTechs.map(Number),
    notes,
    flexible,
    status: "in_progress",
  });
  const inspectionId = res.data.id;

  if (assetIds.length > 0) {
    await axios.post(`${API}/inspection-items/bulk_create/`, {
      inspection: inspectionId,
      asset_ids: assetIds,
    });
  }
  return { id: inspectionId, assetCount: assetIds.length, label: tab.label };
}
